import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type { WithdrawPosition } from "../types";
import { useRewardPools } from "./use-reward-pools";
import { useUserDeposits } from "./use-user-deposits";
import {
  ethCall,
  fetchCrossRpcUrl,
  toBigIntOrUndefined,
} from "./lp-balance-rpc-fallback";

export const WITHDRAW_TOKEN_META_QUERY_KEY = "wp:withdraw-token-meta";

/** `depositToken()` — CrossGameRewardPool의 예치 토큰 주소. */
const DEPOSIT_TOKEN_SELECTOR = "0xc89039c5";
/** `symbol()` / `decimals()` — ERC20 메타. */
const SYMBOL_SELECTOR = "0x95d89b41";
const DECIMALS_SELECTOR = "0x313ce567";

interface DepositTokenMeta {
  tokenAddress: string;
  symbol: string;
  decimals: number;
}

/** ABI 인코딩된 string 반환값(offset+length+bytes) 디코드. 실패 시 undefined. */
function decodeAbiString(hex: string | undefined): string | undefined {
  if (!hex || hex === "0x") return undefined;
  try {
    const body = hex.replace(/^0x/, "");
    const length = Number(BigInt("0x" + body.slice(64, 128)));
    if (!Number.isFinite(length) || length <= 0 || length > 64) return undefined;
    const bytes = body.slice(128, 128 + length * 2);
    let out = "";
    for (let i = 0; i < bytes.length; i += 2) {
      out += String.fromCharCode(parseInt(bytes.slice(i, i + 2), 16));
    }
    return out || undefined;
  } catch {
    return undefined;
  }
}

/** 32바이트 word 뒤 20바이트를 address로 디코드. */
function decodeAbiAddress(hex: string | undefined): string | undefined {
  if (!hex || hex === "0x") return undefined;
  const body = hex.replace(/^0x/, "").padStart(64, "0");
  const address = "0x" + body.slice(24, 64);
  return /^0x[a-f0-9]{40}$/.test(address) &&
    address !== "0x0000000000000000000000000000000000000000"
    ? address
    : undefined;
}

/**
 * `/v1/pools`에 없는(비활성/quest) 풀의 예치 토큰 메타를 온체인에서 읽는다.
 * pool.depositToken() → ERC20 symbol()/decimals(). LP RPC 폴백과 동일하게
 * chain-info API가 알려준 CROSS RPC로 raw eth_call 한다.
 */
async function readDepositTokenMeta(
  env: Environment,
  poolAddresses: string[],
  signal?: AbortSignal,
): Promise<Record<string, DepositTokenMeta>> {
  if (poolAddresses.length === 0) return {};

  let rpcUrl: string | undefined;
  try {
    rpcUrl = await fetchCrossRpcUrl(env, signal);
  } catch {
    rpcUrl = undefined;
  }
  if (!rpcUrl) return {};

  const entries = await Promise.all(
    poolAddresses.map(async (pool) => {
      const tokenAddress = decodeAbiAddress(
        await ethCall(rpcUrl as string, pool, DEPOSIT_TOKEN_SELECTOR, signal),
      );
      if (!tokenAddress) return [pool.toLowerCase(), null] as const;

      const [symbolHex, decimalsHex] = await Promise.all([
        ethCall(rpcUrl as string, tokenAddress, SYMBOL_SELECTOR, signal),
        ethCall(rpcUrl as string, tokenAddress, DECIMALS_SELECTOR, signal),
      ]);
      const symbol = decodeAbiString(symbolHex);
      const decimalsRaw = toBigIntOrUndefined(decimalsHex);
      if (!symbol) return [pool.toLowerCase(), null] as const;

      return [
        pool.toLowerCase(),
        {
          tokenAddress,
          symbol,
          decimals: decimalsRaw !== undefined ? Number(decimalsRaw) : 18,
        },
      ] as const;
    }),
  );

  const out: Record<string, DepositTokenMeta> = {};
  for (const [addr, meta] of entries) {
    if (meta) out[addr] = meta;
  }
  return out;
}

/**
 * 사용자가 출금할 수 있는 예치 포지션(deposited > 0) 전체.
 *
 * 데이터 소스는 공개 reward API 두 개의 합성:
 * - `/v1/users/:addr/deposits` — 사용자가 예치한 모든 풀(비활성/quest 풀 포함)
 * - `/v1/pools` — 활성 풀의 토큰 메타(symbol/decimals/price)
 *
 * deposits에는 있는데 pools에는 없는 풀(cross-points 예치 퀘스트 풀 등)은
 * 온체인 RPC로 depositToken 메타를 보강한다. 메타 확보에 실패한 풀은
 * 금액 해석(decimals)이 불가능하므로 목록에서 제외한다.
 */
export function useWithdrawPositions(
  env: Environment,
  walletAddress: string,
  enabled = true,
) {
  const hasAddress = !!walletAddress;
  const isEnabled = enabled && hasAddress;
  const { allPools, isLoading: poolsLoading } = useRewardPools(env, isEnabled);
  const { deposits, isLoading: depositsLoading } = useUserDeposits(
    env,
    walletAddress,
    isEnabled,
  );

  const poolMap = useMemo(() => {
    const map = new Map<string, (typeof allPools)[number]>();
    for (const pool of allPools) map.set(pool.pool_address.toLowerCase(), pool);
    return map;
  }, [allPools]);

  const positiveDeposits = useMemo(
    () =>
      deposits.filter((d) => {
        try {
          return BigInt(d.deposited_amount || "0") > 0n;
        } catch {
          return false;
        }
      }),
    [deposits],
  );

  // pools API가 모르는 풀만 RPC 메타 조회 대상.
  const unknownPoolAddresses = useMemo(
    () =>
      positiveDeposits
        .map((d) => d.pool_address.toLowerCase())
        .filter((addr) => !poolMap.has(addr))
        .sort(),
    [positiveDeposits, poolMap],
  );

  const { data: rpcMeta, isLoading: rpcMetaLoading } = useQuery({
    queryKey: [WITHDRAW_TOKEN_META_QUERY_KEY, env, unknownPoolAddresses],
    queryFn: ({ signal }) =>
      readDepositTokenMeta(env, unknownPoolAddresses, signal),
    enabled: isEnabled && unknownPoolAddresses.length > 0,
    // 토큰 메타(symbol/decimals)는 불변 — 포트폴리오 주기 폴링에서 제외.
    staleTime: 24 * 60 * 60 * 1000,
  });

  const positions = useMemo<WithdrawPosition[]>(() => {
    const out: WithdrawPosition[] = [];
    for (const deposit of positiveDeposits) {
      const key = deposit.pool_address.toLowerCase();
      const pool = poolMap.get(key);
      if (pool) {
        out.push({
          poolId: deposit.pool_id,
          poolAddress: deposit.pool_address,
          poolType: pool.pool_type,
          tokenSymbol: pool.deposit_token.symbol,
          tokenAddress: pool.deposit_token.address,
          decimals: pool.deposit_token.decimals,
          price: pool.deposit_token.price,
          depositedRaw: deposit.deposited_amount,
        });
        continue;
      }
      const meta = rpcMeta?.[key];
      if (meta) {
        out.push({
          poolId: deposit.pool_id,
          poolAddress: deposit.pool_address,
          poolType: "Unknown",
          tokenSymbol: meta.symbol,
          tokenAddress: meta.tokenAddress,
          decimals: meta.decimals,
          depositedRaw: deposit.deposited_amount,
        });
      }
      // meta 미확보 풀은 decimals를 몰라 금액을 안전하게 다룰 수 없어 제외.
    }
    return out;
  }, [positiveDeposits, poolMap, rpcMeta]);

  // 최초 조회가 하나라도 진행 중이면 positions를 확정 상태로 보지 않는다.
  const isLoading = poolsLoading || depositsLoading || rpcMetaLoading;

  return { positions, isLoading };
}
